/* =========================================================================
   Oppie cookie consent
   Drives a custom, brand-styled consent banner on top of Shopify's
   Customer Privacy API. Consent set here propagates to Shopify-managed
   surfaces (web pixels, audiences, checkout) and, via Shopify's Google
   channel, to Google Consent Mode v2 — so no analytics/gtag is hardcoded.

   Categories map onto the API consent object:
     necessary   -> always allowed (never sent)
     preferences -> preferences
     analytics   -> analytics
     marketing   -> marketing + sale_of_data
   ========================================================================= */

const FEATURE = [{ name: 'consent-tracking-api', version: '0.1' }];
const HIDE_MS = 360; // keep in sync with --dur-slow
const READY_TIMEOUT_MS = 8000; // give Shopify's runtime time to inject loadFeatures
const READY_POLL_MS = 120;

const root = document.querySelector('[data-oppie-cookie]');

// Allow a non-binding visual preview (?preview_cookies=1) so the banner can be
// reviewed/screenshotted even on stores where consent regions aren't triggered.
const previewMode = new URLSearchParams(window.location.search).has('preview_cookies');

if (root) {
  const banner = root.querySelector('[data-cc-banner]');
  const scrim = root.querySelector('[data-cc-scrim]');
  const dialog = root.querySelector('[data-cc-dialog]');
  const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled])';
  let lastFocused = null;
  const toggles = {
    preferences: root.querySelector('[data-cc-toggle="preferences"]'),
    analytics: root.querySelector('[data-cc-toggle="analytics"]'),
    marketing: root.querySelector('[data-cc-toggle="marketing"]'),
  };

  const privacy = () => window.Shopify?.customerPrivacy;

  const setConsent = (consent) => {
    const api = privacy();
    if (!api?.setTrackingConsent) {
      // The card blocks the page, so a missing API must never strand the
      // visitor behind a scrim they cannot dismiss. Let them through and log
      // it; nothing is consented, which is the safe default.
      console.error('Oppie cookie consent: customerPrivacy API not ready, consent not saved');
      hideBanner();
      return;
    }
    try {
      // Hiding happens on the visitorConsentCollected event (single source of
      // truth), so the banner only disappears once consent is actually recorded.
      api.setTrackingConsent(consent, (result) => {
        if (result?.error) {
          console.error('Oppie cookie consent: setTrackingConsent error', result.error);
        }
      });
    } catch (error) {
      console.error('Oppie cookie consent: setTrackingConsent failed', error);
    }
  };

  const fromCategories = ({ preferences, analytics, marketing }) => ({
    preferences,
    analytics,
    marketing,
    sale_of_data: marketing,
  });

  const acceptAll = () =>
    setConsent({ preferences: true, analytics: true, marketing: true, sale_of_data: true });

  const rejectAll = () =>
    setConsent({ preferences: false, analytics: false, marketing: false, sale_of_data: false });

  const saveSelection = () =>
    setConsent(
      fromCategories({
        preferences: !!toggles.preferences?.checked,
        analytics: !!toggles.analytics?.checked,
        marketing: !!toggles.marketing?.checked,
      })
    );

  // ----- banner visibility -----
  // The banner is a blocking modal: scrim over the page, scroll locked, focus
  // held inside it. Every exit runs through a consent choice, which is the
  // whole point — an ignorable banner leaves the visitor unconsented and
  // therefore unmeasurable.

  // Floating widgets ride their own z-index above the scrim: Shopify's chat
  // launcher sits at 2147483000, and our discount popup can open its own modal
  // on a 20s timer straight over the consent card. Park both until a choice is
  // made, otherwise the "blocking" card does not actually block.
  //
  // Inline styles, not a CSS rule: <shopify-chat> never appears in theme
  // markup, so Shopify's live CSS subset pruner would strip a stylesheet rule
  // that targets it (see oppie-fullscreen-video.js for the same workaround).
  const OVERLAY_WIDGETS = 'shopify-chat, #shopify-chat, shopify-agent, [data-oppie-discount]';
  let widgetObserver = null;

  const hideOverlayWidgets = () => {
    document
      .querySelectorAll(OVERLAY_WIDGETS)
      .forEach((el) => el.style.setProperty('display', 'none', 'important'));
  };

  const restoreOverlayWidgets = () => {
    widgetObserver?.disconnect();
    widgetObserver = null;
    document
      .querySelectorAll(OVERLAY_WIDGETS)
      .forEach((el) => el.style.removeProperty('display'));
  };

  // The launcher is injected asynchronously and often lands after the banner is
  // already up, so hiding once is not enough — watch for it while we block.
  const watchOverlayWidgets = () => {
    hideOverlayWidgets();
    widgetObserver = new MutationObserver(hideOverlayWidgets);
    widgetObserver.observe(document.body, { childList: true });
  };

  // Keep focus inside the card. Tab cycles; Escape does nothing, because
  // dismissing without choosing is exactly what we are removing.
  const trapFocus = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      return;
    }
    if (event.key !== 'Tab' || !banner) return;
    const items = [...banner.querySelectorAll(FOCUSABLE)].filter(
      (el) => el.offsetParent !== null
    );
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === banner)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const showBanner = () => {
    if (!banner) return;
    lastFocused = document.activeElement;
    if (scrim) scrim.hidden = false;
    banner.hidden = false;
    document.documentElement.classList.add('oppie-cookie-locked');
    watchOverlayWidgets();
    banner.addEventListener('keydown', trapFocus);
    requestAnimationFrame(() => {
      banner.classList.add('is-visible');
      if (scrim) scrim.classList.add('is-visible');
      // Focus the card itself, not the first button: announcing the title and
      // body to a screen reader matters more than landing on "Weigeren".
      banner.focus({ preventScroll: true });
    });
  };

  const hideBanner = () => {
    if (!banner) return;
    banner.classList.remove('is-visible');
    if (scrim) scrim.classList.remove('is-visible');
    banner.removeEventListener('keydown', trapFocus);
    document.documentElement.classList.remove('oppie-cookie-locked');
    restoreOverlayWidgets();
    window.setTimeout(() => {
      banner.hidden = true;
      if (scrim) scrim.hidden = true;
    }, HIDE_MS);
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus({ preventScroll: true });
      lastFocused = null;
    }
  };

  // ----- preferences dialog -----
  const syncToggles = () => {
    const current = privacy()?.currentVisitorConsent?.() ?? {};
    if (toggles.preferences) toggles.preferences.checked = current.preferences === 'yes';
    if (toggles.analytics) toggles.analytics.checked = current.analytics === 'yes';
    if (toggles.marketing) toggles.marketing.checked = current.marketing === 'yes';
  };

  const openPrefs = () => {
    syncToggles();
    if (typeof dialog?.showModal === 'function') dialog.showModal();
  };

  const closePrefs = () => {
    if (dialog?.open) dialog.close();
  };

  // ----- wire controls -----
  root.querySelector('[data-cc-accept]')?.addEventListener('click', acceptAll);

  root.querySelector('[data-cc-prefs]')?.addEventListener('click', openPrefs);

  root.querySelector('[data-cc-close]')?.addEventListener('click', closePrefs);

  root.querySelector('[data-cc-accept-all]')?.addEventListener('click', () => {
    acceptAll();
    closePrefs();
  });

  root.querySelector('[data-cc-save]')?.addEventListener('click', () => {
    saveSelection();
    closePrefs();
  });

  // Re-open from anywhere (e.g. a footer "Cookievoorkeuren" link) so consent
  // can be withdrawn as easily as it was given (GDPR).
  const reopen = (event) => {
    event?.preventDefault();
    openPrefs();
  };
  document.querySelectorAll('[data-oppie-cookie-settings]').forEach((el) => {
    el.addEventListener('click', reopen);
  });
  window.OppieCookies = { open: openPrefs, accept: acceptAll, reject: rejectAll };

  // Shopify fires this whenever consent changes (incl. our own setTrackingConsent).
  // It is the reliable signal that a choice has been recorded — hide on it.
  document.addEventListener('visitorConsentCollected', () => {
    if (!previewMode) hideBanner();
  });

  // A visitor has made a choice once any category is explicitly 'yes' or 'no'.
  // This is our single gate for showing the banner. We deliberately do NOT rely
  // on shouldShowBanner(): that flag is tied to Shopify's own (now removed)
  // cookie banner config, and has also been observed to keep returning true even
  // after consent is recorded. Gating purely on "no decision yet" keeps our
  // banner working independently of any Shopify-side banner setting.
  const consentDecisionMade = () => {
    const c = privacy()?.currentVisitorConsent?.() || {};
    return ['analytics', 'marketing', 'preferences', 'sale_of_data'].some(
      (key) => c[key] === 'yes' || c[key] === 'no'
    );
  };

  // ----- decide whether to show the banner -----
  // decide() only runs once the Customer Privacy API is available (see onReady),
  // so setTrackingConsent will work when the visitor chooses. Show whenever no
  // choice has been recorded yet — no dependency on Shopify's shouldShowBanner().
  const decide = () => {
    if (previewMode) {
      showBanner();
      return;
    }
    if (consentDecisionMade()) return;
    showBanner();
  };

  // ----- wait for Shopify's Customer Privacy runtime, then decide -----
  // The module is deferred/low-priority, so window.Shopify.loadFeatures may not
  // exist yet on first run. Polling for it (instead of giving up immediately)
  // fixes the intermittent "banner never appears" bug.
  const onReady = () => {
    if (typeof window.Shopify?.loadFeatures !== 'function') {
      // Runtime present but loadFeatures missing — try the API directly.
      if (privacy()) decide();
      else if (previewMode) showBanner();
      return;
    }
    window.Shopify.loadFeatures(FEATURE, (error) => {
      if (error) {
        console.error('Oppie cookie consent: failed to load consent-tracking-api', error);
        if (previewMode) showBanner();
        return;
      }
      decide();
    });
  };

  const waitForRuntime = () => {
    const start = performance.now();
    const tick = () => {
      if (privacy() || typeof window.Shopify?.loadFeatures === 'function') {
        onReady();
        return;
      }
      if (performance.now() - start >= READY_TIMEOUT_MS) {
        if (previewMode) showBanner();
        return;
      }
      window.setTimeout(tick, READY_POLL_MS);
    };
    tick();
  };

  waitForRuntime();
}
